// assets/variant-selects-custom.js (Versión 2 con depuración y URL)

const SALE_PRICE_LABELS = {
  price: "Price",
  sale: "Sale price",
  regular: "Regular price",
};

function formatSalePriceCents(cents) {
  const amount = Number(cents) / 100;
  return `$${amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2)}`;
}

/**
 * Renders the snippets/sale-price.liquid markup for a variant on the client.
 * Used only when no server-rendered replacement is available.
 * @param {HTMLElement} element - The [data-sale-price-sync] root.
 * @param {{price: number|string, compare_at_price?: number|string|null}} variant
 * @param {(cents: number) => string} [format]
 */
function renderSalePrice(element, variant, format = formatSalePriceCents) {
  if (!element || !variant) return;

  const price = Number(variant.price) || 0;
  const compare = Number(variant.compare_at_price) || 0;
  const onSale = compare > price;

  const label = (text) => {
    const span = document.createElement("span");
    span.className = "visually-hidden";
    span.textContent = text;
    return span;
  };

  const current = document.createElement("span");
  current.className = "sale-price__current";
  current.dataset.testid = "price-current";
  current.textContent = format(price);

  if (!onSale) {
    element.replaceChildren(label(SALE_PRICE_LABELS.price), current);
    return;
  }

  const original = document.createElement("s");
  original.className = "sale-price__original";
  original.dataset.testid = "price-original";
  original.textContent = format(compare);

  const nodes = [label(SALE_PRICE_LABELS.sale), current, label(SALE_PRICE_LABELS.regular), original];
  const percent = Math.round(((compare - price) * 100) / compare);

  if (percent >= 1) {
    const badge = document.createElement("span");
    badge.className = "sale-price__badge";
    badge.dataset.testid = "price-badge";
    badge.textContent = `-${percent}%`;
    nodes.push(badge);
  }

  element.replaceChildren(...nodes);
}

/*
 * Horizon's <variant-picker> fetches the page for the selected variant and
 * dispatches `variant:update` with the parsed document. Every price rendered
 * with a sync_key (PDP header, sticky bar) is swapped for its fresh
 * server-rendered copy, falling back to the variant JSON.
 */
document.addEventListener("variant:update", (event) => {
  const data = event.detail?.data;
  const variant = event.detail?.resource;
  const productId = String(data?.productId ?? "");

  document.querySelectorAll("[data-sale-price-sync]").forEach((element) => {
    if (element.dataset.productId && productId && element.dataset.productId !== productId) return;

    const key = CSS.escape(element.dataset.salePriceSync);
    const fresh = data?.html?.querySelector(`[data-sale-price-sync="${key}"]`);

    if (fresh) {
      if (fresh.innerHTML !== element.innerHTML) element.replaceWith(document.importNode(fresh, true));
    } else if (variant && "price" in variant) {
      renderSalePrice(element, variant);
    }
  });
});

if (!customElements.get("variant-selects-custom")) {
  class VariantSelectsCustom extends HTMLElement {
    constructor() {
      super();
      this.addEventListener("change", this.onVariantChange.bind(this));
    }

    connectedCallback() {
      // Usamos un pequeño retraso para asegurar que todo el DOM esté listo
      setTimeout(() => {
        this.variants = JSON.parse(this.querySelector("script").textContent);
        // Forzamos una actualización inicial para que la página cargue con el estado correcto
        this.onVariantChange();
      }, 0);
    }

    onVariantChange() {
      this.currentVariant = this.getCurrentVariant();

      if (this.currentVariant) {
        // --- Lógica para actualizar la página ---
        this.updateURL();
        this.updateProductFormId();
        this.updateAddToCartButton();
        this.updatePrice();

        // Emitir el evento para la galería de medios
        this.dispatchEvent(
          new CustomEvent("variant:change", {
            bubbles: true,
            detail: {
              variant: this.currentVariant,
            },
          })
        );
      } else {
        console.error(
          "ERROR: No se encontró una variante para la combinación seleccionada."
        );
        this.updateAddToCartButton(false); // Deshabilitar botón si no hay variante
      }
    }

    getCurrentVariant() {
      const selectedOptions = Array.from(
        this.querySelectorAll('input[type="radio"]:checked')
      ).map((input) => input.value);

      return this.variants.find((variant) => {
        return variant.options.every(
          (optionValue, index) => optionValue === selectedOptions[index]
        );
      });
    }

    updateURL() {
      if (!this.currentVariant) return;
      // Actualiza la URL en la barra de direcciones sin recargar la página
      window.history.replaceState(
        {},
        "",
        `${this.dataset.url}?variant=${this.currentVariant.id}`
      );
    }

    updateProductFormId() {
      // Búsqueda robusta: encuentra el <form> más cercano que contenga a nuestro <variant-selects-custom>
      // O busca un formulario con el tipo 'product' en toda la página.
      const productForm =
        this.closest('form[action^="/cart/add"]') ||
        document.querySelector('form[action^="/cart/add"]');

      if (!productForm) {
        console.error(
          'ERROR DE SELECTOR: No se encontró ningún formulario de producto (form[action^="/cart/add"]).'
        );
        return;
      }

      const formInput = productForm.querySelector('input[name="id"]');
      if (formInput) {
        formInput.value = this.currentVariant.id;
      } else {
        console.error(
          'ERROR DE SELECTOR: No se encontró input[name="id"] dentro del formulario.',
          productForm
        );
      }
    }

    updateAddToCartButton() {
      const productForm =
        this.closest('form[action^="/cart/add"]') ||
        document.querySelector('form[action^="/cart/add"]');
      if (!productForm) return;

      const addButton = productForm.querySelector('button[name="add"]');
      if (!addButton) {
        console.error(
          'ERROR DE SELECTOR: No se encontró el botón de "Agregar al carrito".'
        );
        return;
      }

      const addButtonText = addButton.querySelector(".button__text"); // Ajusta si la clase es diferente

      if (this.currentVariant && this.currentVariant.available) {
        addButton.disabled = false;
        if (addButtonText) addButtonText.textContent = "Add to Bag"; // Reemplaza con tu texto/traducción
      } else {
        addButton.disabled = true;
        if (addButtonText) addButtonText.textContent = "Sold Out"; // Reemplaza con tu texto/traducción
      }
    }

    updatePrice() {
      document.querySelectorAll("[data-sale-price-sync]").forEach((element) => {
        renderSalePrice(element, this.currentVariant, (cents) => this.formatMoney(cents));
      });
    }

    /**
     * Helper simple para formatear el precio.
     * Deberías adaptarlo al formato de tu tienda si es necesario.
     */
    formatMoney(cents, format) {
      if (typeof cents == "string") {
        cents = cents.replace(".", "");
      }
      let value = "";
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || "${{amount}}";

      function defaultOption(opt, def) {
        return typeof opt == "undefined" ? def : opt;
      }

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultOption(precision, 2);
        thousands = defaultOption(thousands, ",");
        decimal = defaultOption(decimal, ".");

        if (isNaN(number) || number == null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        let parts = number.split(".");
        let dollars = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          "$1" + thousands
        );
        let cents = parts[1] ? decimal + parts[1] : "";

        return dollars + cents;
      }

      // Verificar si tiene decimales
      const hasDecimals = (cents % 100) !== 0;
      const dynamicPrecision = hasDecimals ? 2 : 0;

      switch (formatString.match(placeholderRegex)[1]) {
        case "amount":
          value = formatWithDelimiters(cents, dynamicPrecision);
          break;
        case "amount_no_decimals":
          value = formatWithDelimiters(cents, 0);
          break;
        case "amount_with_comma_separator":
          value = formatWithDelimiters(cents, dynamicPrecision, ".", ",");
          break;
        case "amount_no_decimals_with_comma_separator":
          value = formatWithDelimiters(cents, 0, ".", ",");
          break;
      }
      return formatString.replace(placeholderRegex, value);
    }
  }

  customElements.define("variant-selects-custom", VariantSelectsCustom);
}
